/**
 * Outil « Balance des équations » — calculs (pur).
 *
 * Une équation (ou une inéquation) du premier degré à coefficients entiers,
 * ax + b R cx + d. Chaque geste s'applique AUX DEUX CÔTÉS : ajouter ou
 * enlever un nombre, ajouter ou enlever des x, diviser par un même nombre
 * (seulement si chaque côté se partage exactement), changer les signes.
 * Diviser ou multiplier par un négatif RETOURNE une inégalité. Conseil du
 * geste suivant ; vérification de la solution ; génération selon le niveau.
 */

export type Rel = '=' | '<' | '>' | '≤' | '≥';

export interface Side {
  /** coefficient de x */
  x: number;
  /** constante */
  c: number;
}

export interface Eq {
  l: Side;
  r: Side;
  rel: Rel;
}

export type Move =
  | { kind: 'add'; k: number }
  | { kind: 'addX'; k: number }
  | { kind: 'div'; k: number }
  | { kind: 'neg' }
  | { kind: 'swap' };

/** bornes des coefficients (lisibilité, dessin) */
export const MAX_X = 20;
export const MAX_C = 99;

const FLIP: Record<Rel, Rel> = { '=': '=', '<': '>', '>': '<', '≤': '≥', '≥': '≤' };
const REL_TEX: Record<Rel, string> = { '=': '=', '<': '<', '>': '>', '≤': '\\leq', '≥': '\\geq' };

export function relTex(rel: Rel): string {
  return REL_TEX[rel];
}

/** « 2x + 3 », « -x », « 5 », « 0 » */
export function sideTex(s: Side): string {
  const xs = s.x === 0 ? '' : s.x === 1 ? 'x' : s.x === -1 ? '-x' : `${s.x}x`;
  if (!xs) return String(s.c);
  if (s.c === 0) return xs;
  return `${xs} ${s.c < 0 ? '-' : '+'} ${Math.abs(s.c)}`;
}

export function eqTex(e: Eq): string {
  return `${sideTex(e.l)} ${REL_TEX[e.rel]} ${sideTex(e.r)}`;
}

/** le même texte, lisible à voix haute / en texte brut : « 2x + 3 = x + 8 » */
export function eqPlain(e: Eq): string {
  return eqTex(e).replace('\\leq', '≤').replace('\\geq', '≥').replace(/-/g, '−');
}

/** « − 3 », « + 2x », « ÷ 2 », « × (−1) », « on échange les côtés » */
export function moveLabel(m: Move): string {
  switch (m.kind) {
    case 'add':
      return `${m.k < 0 ? '−' : '+'} ${Math.abs(m.k)}`;
    case 'addX':
      return `${m.k < 0 ? '−' : '+'} ${Math.abs(m.k) === 1 ? '' : Math.abs(m.k)}x`;
    case 'div':
      return `÷ ${m.k < 0 ? `(−${-m.k})` : m.k}`;
    case 'neg':
      return '× (−1)';
    case 'swap':
      return 'on échange les côtés';
  }
}

const within = (s: Side) => Math.abs(s.x) <= MAX_X && Math.abs(s.c) <= MAX_C;

/** Applique le geste aux deux côtés ; null s'il n'est pas possible (division pas exacte…). */
export function apply(e: Eq, m: Move): Eq | null {
  let next: Eq;
  switch (m.kind) {
    case 'add':
      if (m.k === 0) return null;
      next = { ...e, l: { ...e.l, c: e.l.c + m.k }, r: { ...e.r, c: e.r.c + m.k } };
      break;
    case 'addX':
      if (m.k === 0) return null;
      next = { ...e, l: { ...e.l, x: e.l.x + m.k }, r: { ...e.r, x: e.r.x + m.k } };
      break;
    case 'div': {
      if (m.k === 0 || m.k === 1) return null;
      const all = [e.l.x, e.l.c, e.r.x, e.r.c];
      if (all.some((v) => v % m.k !== 0)) return null;
      next = {
        l: { x: e.l.x / m.k + 0, c: e.l.c / m.k + 0 },
        r: { x: e.r.x / m.k + 0, c: e.r.c / m.k + 0 },
        rel: m.k < 0 ? FLIP[e.rel] : e.rel,
      };
      break;
    }
    case 'neg':
      next = {
        l: { x: -e.l.x + 0, c: -e.l.c + 0 },
        r: { x: -e.r.x + 0, c: -e.r.c + 0 },
        rel: FLIP[e.rel],
      };
      break;
    case 'swap':
      next = { l: e.r, r: e.l, rel: FLIP[e.rel] };
      break;
  }
  return within(next.l) && within(next.r) ? next : null;
}

/** pourquoi un geste est refusé (pour le dire à l'élève) */
export function refusal(e: Eq, m: Move): string | null {
  if (apply(e, m)) return null;
  if (m.kind === 'div' && (m.k === 0 || m.k === 1)) return null;
  if (m.kind === 'div')
    return `Chaque côté ne se partage pas exactement en ${Math.abs(m.k)} : ce geste donnerait des fractions.`;
  return 'Les nombres deviendraient trop grands pour la balance.';
}

export const evalSide = (s: Side, x: number) => s.x * x + s.c;

/** l'inégalité (ou l'égalité) est-elle vraie pour cette valeur de x ? */
export function holds(e: Eq, x: number): boolean {
  const a = evalSide(e.l, x);
  const b = evalSide(e.r, x);
  switch (e.rel) {
    case '=':
      return a === b;
    case '<':
      return a < b;
    case '>':
      return a > b;
    case '≤':
      return a <= b;
    case '≥':
      return a >= b;
  }
}

/**
 * Résolue ? « x R valeur » quand un côté vaut exactement x et l'autre un
 * nombre (x écrit à droite : on lit la relation à l'envers).
 */
export function solved(e: Eq): { rel: Rel; value: number } | null {
  const isX = (s: Side) => s.x === 1 && s.c === 0;
  const isConst = (s: Side) => s.x === 0;
  if (isX(e.l) && isConst(e.r)) return { rel: e.rel, value: e.r.c };
  if (isX(e.r) && isConst(e.l)) return { rel: FLIP[e.rel], value: e.l.c };
  return null;
}

/** plus de x d'un côté ni de l'autre : toujours vrai, ou jamais */
export function degenerate(e: Eq): 'always' | 'never' | null {
  if (e.l.x !== 0 || e.r.x !== 0) return null;
  return holds(e, 0) ? 'always' : 'never';
}

const minus = (n: number) => (n < 0 ? `−${-n}` : String(n));
const xs = (n: number) => (Math.abs(n) === 1 ? 'x' : `${Math.abs(n)}x`);

/**
 * Le geste conseillé, avec sa raison, jouable au niveau donné : pas de
 * changement de signe en 5e (rang 2) ; on garde des x positifs.
 */
export function hint(e: Eq, rank = 5): { move: Move; why: string } | null {
  if (solved(e) || degenerate(e)) return null;
  const { l, r } = e;
  // 1. des x des deux côtés : on retire les x du côté qui en a le moins
  if (l.x !== 0 && r.x !== 0) {
    if (l.x === r.x)
      return {
        move: { kind: 'addX', k: -l.x },
        why: `On ${l.x > 0 ? 'enlève' : 'ajoute'} ${xs(l.x)} des deux membres : il ne restera plus de x.`,
      };
    const fromRight = l.x > r.x;
    const k = fromRight ? -r.x : -l.x;
    return {
      move: { kind: 'addX', k },
      why: `On ${k < 0 ? 'enlève' : 'ajoute'} ${xs(k)} des deux membres : il ne restera des x qu’${fromRight ? 'à gauche' : 'à droite'}.`,
    };
  }
  const side = l.x !== 0 ? l : r;
  const where = l.x !== 0 ? 'à gauche' : 'à droite';
  // 2. le nombre à côté des x passe de l'autre côté
  if (side.c !== 0)
    return {
      move: { kind: 'add', k: -side.c },
      why: `On ${side.c > 0 ? 'enlève' : 'ajoute'} ${Math.abs(side.c)} aux deux membres : les x restent seuls ${where}.`,
    };
  // 3. en 5e, pas de signes à changer : on fait passer les −x de l'autre côté
  if (side.x < 0 && rank <= 2)
    return {
      move: { kind: 'addX', k: -side.x },
      why: `On ajoute ${xs(side.x)} aux deux membres : les x passent de l’autre côté, sans signe −.`,
    };
  // 4. un seul x
  if (side.x === -1)
    return {
      move: { kind: 'neg' },
      why:
        e.rel === '='
          ? 'On change les signes des deux membres : −x devient x.'
          : 'On change les signes des deux membres : −x devient x, et le sens de l’inégalité se retourne.',
    };
  return {
    move: { kind: 'div', k: side.x },
    why:
      side.x < 0 && e.rel !== '='
        ? `On divise les deux membres par ${minus(side.x)} : c’est un nombre négatif, le sens de l’inégalité se retourne.`
        : `On divise les deux membres par ${minus(side.x)} : il reste un seul x.`,
  };
}

/**
 * Une équation (ou inéquation) à solution entière, adaptée au niveau
 * (rangs : 5e = 2, 4e = 3, 3e = 4, 2nde = 5). `rng` dans [0 ; 1[.
 */
export function generate(rank: number, rng: () => number, inequality = false): Eq {
  const int = (lo: number, hi: number) => lo + Math.floor(rng() * (hi - lo + 1));
  const rels: Rel[] = ['<', '>', '≤', '≥'];
  const rel: Rel = inequality ? rels[int(0, 3)]! : '=';
  const nonZero = [-5, -4, -3, -2, 2, 3, 4, 5, 6];
  for (let tries = 0; tries < 200; tries++) {
    let e: Eq;
    if (rank <= 2) {
      // 5e : x + b = c ou ax = c, nombres positifs (opérations inverses)
      const s = int(1, 10);
      e = int(0, 1)
        ? { l: { x: 1, c: int(1, 15) }, r: { x: 0, c: 0 }, rel }
        : { l: { x: int(2, 9), c: 0 }, r: { x: 0, c: 0 }, rel };
      e.r.c = e.l.x * s + e.l.c;
    } else if (rank === 3) {
      // 4e : ax + b = c, a, b, c entiers relatifs
      const s = int(-6, 9);
      const a = nonZero[int(0, 8)]!;
      const b = int(-12, 15);
      e = { l: { x: a, c: b }, r: { x: 0, c: a * s + b }, rel };
    } else if (rank === 4 && inequality) {
      // 3e : inéquations du type ax + b ≥ c (x d'un seul côté)
      const s = int(-6, 9);
      const a = nonZero[int(0, 8)]!;
      const b = int(-10, 10);
      e = { l: { x: a, c: b }, r: { x: 0, c: a * s + b }, rel };
    } else {
      // 3e (équations), 2nde : x des deux côtés, signes quelconques
      const s = int(-6, 9);
      const a = nonZero[int(0, 8)]!;
      const c = int(-3, 4);
      const b = int(-12, 15);
      e = { l: { x: a, c: b }, r: { x: c, c: (a - c) * s + b }, rel };
    }
    if (e.l.x === e.r.x) continue;
    if (Math.abs(e.r.c) > 40 || Math.abs(e.l.c) > 40) continue;
    if (solved(e)) continue;
    return e;
  }
  return { l: { x: 2, c: 3 }, r: { x: 1, c: 8 }, rel };
}

/** l'équation de départ selon le niveau */
export function startFor(rank: number): Eq {
  if (rank <= 2) return { l: { x: 1, c: 7 }, r: { x: 0, c: 12 }, rel: '=' };
  if (rank === 3) return { l: { x: 3, c: -4 }, r: { x: 0, c: 11 }, rel: '=' };
  return { l: { x: 2, c: 3 }, r: { x: 1, c: 8 }, rel: '=' };
}

/**
 * Il ne reste que kx R c, mais k ne divise pas c : la solution est la
 * fraction c/k (réduite, signe au numérateur), et la relation se retourne
 * si k < 0 (ou si x est écrit à droite).
 */
export function fractionalSolution(e: Eq): { rel: Rel; num: number; den: number } | null {
  const xOnly = (s: Side) => s.x !== 0 && s.c === 0;
  const left = xOnly(e.l) && e.r.x === 0;
  const right = xOnly(e.r) && e.l.x === 0;
  if (!left && !right) return null;
  const k = left ? e.l.x : e.r.x;
  const c = left ? e.r.c : e.l.c;
  if (c % k === 0) return null;
  let rel = left ? e.rel : FLIP[e.rel];
  if (k < 0) rel = FLIP[rel];
  const g = gcd(Math.abs(c), Math.abs(k));
  const sign = c * k < 0 ? -1 : 1;
  return { rel, num: (sign * Math.abs(c)) / g, den: Math.abs(k) / g };
}

function gcd(a: number, b: number): number {
  while (b) [a, b] = [b, a % b];
  return a || 1;
}

/** la valeur frontière (solution de l'égalité), si elle existe */
export function boundary(e: Eq): number | null {
  const a = e.l.x - e.r.x;
  if (a === 0) return null;
  return (e.r.c - e.l.c) / a + 0;
}
